import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../src/assets/css/style.css";
import { useLocation } from "react-router-dom";
import {
  getCertificateDownload,
  getSignature,
} from "../CertificateService";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const normalizeImageSource = (value, mimeType = "image/jpeg") => {
  if (!value) return "";
  if (
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  return `data:${mimeType};base64,${value}`;
};

const EnglishCertificate = () => {
  const certificateRef = useRef(null);
  const location = useLocation();
  const applicationId = location.state?.applicationId;

  const [certificateDownloadData, setCertificateDownloadData] = useState(null);
  const [signatureData, setSignatureData] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadCertificate = async () => {
      try {
        setError("");

        const certificateResult = await getCertificateDownload(applicationId);
        const response = certificateResult?.data;

        if (!isMounted) return;
        setCertificateDownloadData(response);

        if (response?.GpLGD) {
          const signatureResult = await getSignature(response.GpLGD);

          if (isMounted) {
            setSignatureData(signatureResult?.data || {});
          }
        }
      } catch (loadError) {
        console.error("Unable to load certificate:", loadError);

        if (isMounted) {
          setError("Unable to load the certificate. Please try again.");
        }
      }
    };

    if (applicationId) {
      loadCertificate();
    } else {
      setError("Application ID was not provided.");
    }

    return () => {
      isMounted = false;
    };
  }, [applicationId]);

  const certificateStatements = useMemo(() => {
    if (!certificateDownloadData) return [];

    const response = certificateDownloadData;
    const isMale = response?.Gender === "Mr.";

    const subjectPronoun = isMale ? "He" : "She";
    const objectPronoun = isMale ? "him" : "her";
    const possessivePronoun = isMale ? "His" : "Her";
    const possessiveLower = isMale ? "his" : "her";

    return [
      response?.UnmarriedCertificate > 0
        ? {
            key: "unmarried",
            content: (
              <>
                To the best of my knowledge,{" "}
                <strong>{subjectPronoun}</strong> is currently unmarried.
              </>
            ),
          }
        : null,

      response?.CastCertificate > 0
        ? {
            key: "caste",
            content: (
              <>
                <strong>{subjectPronoun}</strong> belongs to the{" "}
                <strong>{response?.SubCast}</strong> community under the{" "}
                <strong>{response?.Cast}</strong> category.
              </>
            ),
          }
        : null,

      response?.IncomeCertificate > 0
        ? {
            key: "income",
            content: (
              <>
                <strong>{possessivePronoun}</strong> total annual family income
                from all sources for the Financial Year{" "}
                <strong>{response?.IncomeFinYear}</strong> does not exceed Rs.{" "}
                <strong>{response?.AnnualIncome}</strong>/- (Rupees{" "}
                <strong>{response?.AnnualIncomeWord}</strong> ).
              </>
            ),
          }
        : null,

      response?.DistanceCertificate > 0
        ? {
            key: "distance",
            content: (
              <>
                The approximate distance between{" "}
                <strong>{possessiveLower}</strong> residence and{" "}
                <strong>{response?.ResidenceLocation}</strong> is{" "}
                <strong>{response?.ResidenceDistance}</strong> kilometres.
              </>
            ),
          }
        : null,

      response?.SamePersonCertificate > 0
        ? {
            key: "same-person",
            content: (
              <>
                <strong>{response?.Gender}</strong>{" "}
                <strong>{response?.SamePersonOldName}</strong> and{" "}
                <strong>{response?.Gender}</strong>{" "}
                <strong>{response?.SamePersonNewName}</strong> are the names of
                the same person. <strong>{possessivePronoun}</strong>{" "}
                <strong>{response?.RelationGurdian}</strong> is{" "}
                <strong>{response?.GurdianName}</strong>.{" "}
                <strong>{possessivePronoun}</strong> date of birth is{" "}
                <strong>{response?.DOB}</strong>.
              </>
            ),
          }
        : null,

      response?.CharacterCertificate > 0
        ? {
            key: "character",
            content: (
              <>
                <strong>{subjectPronoun}</strong> possesses a commendable moral
                character. I wish <strong>{objectPronoun}</strong> every
                success in life.
              </>
            ),
          }
        : null,
    ].filter(Boolean);
  }, [certificateDownloadData]);

  const qrData = useMemo(() => {
    if (!certificateDownloadData) return "";

    const response = certificateDownloadData;

    const appliedCertificates = [
      response?.CharacterCertificate > 0 ? "Character Certificate" : null,
      response?.IncomeCertificate > 0 ? "Income Certificate" : null,
      response?.ResidenceCertificate > 0 ? "Residential Certificate" : null,
      response?.SamePersonCertificate > 0 ? "Same Person Certificate" : null,
      response?.DistanceCertificate > 0 ? "Distance Certificate" : null,
      response?.UnmarriedCertificate > 0 ? "Unmarried Certificate" : null,
      response?.CastCertificate > 0 ? "Caste Certificate" : null,
    ]
      .filter(Boolean)
      .join(", ");

    return JSON.stringify({
      "Applicant Name": response?.ApplicantName || "",
      "Application ID": response?.ApplicationId || "",
      "Applied Certificate": appliedCertificates,
      "Pradhan Name": response?.PradhanName || "",
      "Pradhan Designation": response?.PradhanDesig || "",
      "Gram Panchayat Name": response?.GPName || "",
      "Block Name": response?.BlockName || "",
      "District Name": response?.District || "",
      "Valid Upto": response?.ValidDate || "",
      redirectUrl: "https://wbpms.in/citizen/#/certificate-check",
    });
  }, [certificateDownloadData]);

  const applicantImageSrc = normalizeImageSource(
    certificateDownloadData?.ApplicantImageBase64,
    "image/jpeg"
  );

  const signatureImageSrc = normalizeImageSource(
    signatureData?.officerSignature,
    "image/png"
  );

  const handleDownload = async () => {
    if (!certificateRef.current || isDownloading) return;

    try {
      setIsDownloading(true);
      setError("");

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imageData = canvas.toDataURL("image/jpeg", 0.96);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pdf.internal.pageSize.getWidth(),
        pdf.internal.pageSize.getHeight(),
        undefined,
        "MEDIUM"
      );

      pdf.save(
        `${certificateDownloadData?.ApplicationId || "certificate"}.pdf`
      );
    } catch (downloadError) {
      console.error("Error generating PDF:", downloadError);
      setError("The PDF could not be generated. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (error && !certificateDownloadData) {
    return <div className="english-cert-message english-cert-error">{error}</div>;
  }

  if (!certificateDownloadData) {
    return <div className="english-cert-message">Loading certificate...</div>;
  }

  const compactClass =
    certificateStatements.length >= 5 ? "english-cert-body--compact" : "";

  return (
    <div className="english-cert-page">
      <div className="english-cert-preview">
        <section ref={certificateRef} className="english-certificate-sheet">
          <div className="english-cert-safe-area">
            <header className="english-cert-header">
            <h1>{certificateDownloadData?.GPName}</h1>
            <p>{certificateDownloadData?.GPAddress}</p>
          </header>

          <div className="english-cert-meta">
            <p>
              Certificate No:{" "}
              <strong>{certificateDownloadData?.ApplicationId}</strong>
            </p>

            <p>
              Date: <strong>{certificateDownloadData?.ApproveDate}</strong>
            </p>
          </div>

          <div className="english-cert-title-wrap">
            <h2 className="english-cert-title">TO WHOM IT MAY CONCERN</h2>
          </div>

          <main className={`english-cert-body ${compactClass}`}>
            <section className="english-cert-applicant-block">
              <div className="english-cert-applicant-copy">
                <p>
                  This is to certify that{" "}
                  <strong>{certificateDownloadData?.Gender}</strong>{" "}
                  <strong>{certificateDownloadData?.ApplicantName}</strong>,{" "}
                  <strong>{certificateDownloadData?.RelationGurdian}</strong>:{" "}
                  <strong>{certificateDownloadData?.GurdianName}</strong>, who
                  resides at{" "}
                  <strong>{certificateDownloadData?.Address}</strong>, P.O.-{" "}
                  <strong>{certificateDownloadData?.PostOffice}</strong>, P.S.-{" "}
                  <strong>{certificateDownloadData?.PoliceStation}</strong>,
                  District-{" "}
                  <strong>{certificateDownloadData?.District}</strong>, PIN-{" "}
                  <strong>{certificateDownloadData?.Pin}</strong>, is known to
                  me.
                </p>

                <p>
                  <strong>{certificateDownloadData?.Gender}</strong>{" "}
                  <strong>{certificateDownloadData?.ApplicantName}</strong> is a
                  permanent resident of{" "}
                  <strong>{certificateDownloadData?.Address}</strong>, P.O.-{" "}
                  <strong>{certificateDownloadData?.PostOffice}</strong>, P.S.-{" "}
                  <strong>{certificateDownloadData?.PoliceStation}</strong>,
                  District-{" "}
                  <strong>{certificateDownloadData?.District}</strong>, PIN-{" "}
                  <strong>{certificateDownloadData?.Pin}</strong>, State- West
                  Bengal.
                </p>
              </div>

              {applicantImageSrc && (
                <div className="english-cert-photo-column">
                  <div className="english-cert-photo">
                    <img
                      src={applicantImageSrc}
                      alt="Certificate applicant"
                      crossOrigin="anonymous"
                    />
                  </div>
                  {/* <span>Applicant</span> */}
                </div>
              )}
            </section>

            {certificateStatements.length > 0 && (
              <section className="english-cert-declaration-block">
                {certificateStatements.map((statement) => (
                  <p key={statement.key}>{statement.content}</p>
                ))}
              </section>
            )}

            <section className="english-cert-auth-block">
              <div className="english-cert-verification">
                <div className="english-cert-qr-frame">
                  <QRCodeCanvas value={qrData} size={108} level="M" />
                </div>
                <div className="english-cert-verification-copy">
                  <strong>Digital Verification</strong>
                  <span>Scan to verify this certificate</span>
                  <small>https://wbpms.in</small>
                  <small>Valid for 6 months</small>
                </div>
              </div>

              <div className="english-cert-authority">
                <div className="english-cert-signature-area">
                  {signatureImageSrc && (
                    <img
                      src={signatureImageSrc}
                      alt="Authorised signature"
                      crossOrigin="anonymous"
                    />
                  )}
                </div>

                <div className="english-cert-authority-line" />

                <p className="english-cert-authority-name">
                  {signatureData?.officerName}
                </p>
                <p>{certificateDownloadData?.PradhanDesig}</p>
                <p>{certificateDownloadData?.GPName}</p>
                <p>
                  {certificateDownloadData?.BlockName},{" "}
                  {certificateDownloadData?.District}
                </p>
              </div>
            </section>
          </main>

            <footer className="english-cert-footer">
              <em>
                To verify the authenticity of this certificate, please visit
                https://wbpms.in/citizen/#/certificate-check
              </em>
            </footer>
          </div>
        </section>
      </div>

      {error && <div className="english-cert-inline-error">{error}</div>}

      <div className="english-cert-actions">
        <button
          type="button"
          onClick={handleDownload}
          className="english-cert-download-button"
          disabled={isDownloading}
        >
          {isDownloading ? "Generating PDF..." : "Download Certificate"}
        </button>
      </div>
    </div>
  );
};

export default EnglishCertificate;
